import React from 'react';
import { createPortal } from 'react-dom';
import type { BoardMember } from 'types/kanban';
import { getInitials } from '../../kanbanUtils';
import {
  useCurrentUserAvatar,
  isCurrentUser as isCurrentUserAvatar,
} from '../../useCurrentUserAvatar';
import styles from './CardDetailMembersPanel.module.scss';

export interface CardDetailMembersPanelProps {
  show: boolean;
  anchor: DOMRect | null;
  boardMembers: BoardMember[];
  cardMemberIds: string[];
  onToggleMember: (memberId: string) => void;
  onClose: () => void;
}

export default function CardDetailMembersPanel({
  show,
  anchor,
  boardMembers,
  cardMemberIds,
  onToggleMember,
  onClose,
}: CardDetailMembersPanelProps) {
  const { currentUserIds, profilePictureUrl } = useCurrentUserAvatar();
  if (!show || !anchor) return null;

  return createPortal(
    <>
      <div className={styles.panelBackdrop} onClick={onClose} aria-hidden />
      <div
        className={styles.membersPanel}
        style={{
          position: 'fixed',
          top: anchor.bottom + 6,
          left: anchor.left,
          minWidth: 260,
        }}
        role="dialog"
        aria-label="Members"
      >
        {boardMembers.length === 0 ? (
          <p className={styles.membersPanelEmpty}>
            No board members yet. Add from (⋯) Board members.
          </p>
        ) : (
          <ul className={styles.membersPanelList}>
            {boardMembers.map((member) => {
              const isAssigned = cardMemberIds.includes(member.id);
              const resolvedAvatarUrl =
                member.profilePictureUrl ??
                (isCurrentUserAvatar(currentUserIds, member.userId)
                  ? profilePictureUrl
                  : null);
              return (
                <li key={member.id} className={styles.membersPanelItem}>
                  <button
                    type="button"
                    className={styles.memberRowBtn}
                    onClick={() => onToggleMember(member.id)}
                  >
                    {resolvedAvatarUrl ? (
                      <img
                        src={resolvedAvatarUrl}
                        alt=""
                        className={styles.memberAvatarImg}
                        aria-hidden
                      />
                    ) : (
                      <span className={styles.memberAvatar}>
                        {getInitials(member.name)}
                      </span>
                    )}
                    <span className={styles.memberName}>{member.name}</span>
                    {isAssigned && (
                      <span className={styles.memberCheck}>✓</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>,
    document.body,
  );
}
